"use client"

import { cn } from "@coval/ui/cn"

import type { SlashCommand } from "../utils/slashCommands"

/**
 * Speed-dial popup over the composer: the filtered slash commands with
 * one-line descriptions. Pure presentation — the composer owns the keyboard
 * interaction (arrows move the highlight, Enter applies, Tab completes).
 */
export function SlashCommandMenu({
  commands,
  activeIndex,
  onHighlight,
  onSelect,
}: {
  commands: SlashCommand[]
  activeIndex: number
  onHighlight: (index: number) => void
  onSelect: (command: SlashCommand) => void
}) {
  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      className="absolute inset-x-3 bottom-full z-10 mb-1.5 overflow-hidden rounded-xl border border-coval-border bg-surface-default py-1 shadow-lg"
    >
      {commands.map((command, index) => (
        <button
          key={command.command}
          type="button"
          role="option"
          aria-selected={index === activeIndex}
          onMouseEnter={() => onHighlight(index)}
          // Keep focus in the composer so selection lands back in the textarea.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(command)}
          className={cn(
            "flex w-full items-baseline gap-2 px-3 py-1.5 text-left",
            index === activeIndex && "bg-surface-2",
          )}
        >
          <span className="shrink-0 text-xs font-medium text-coval-text">
            {command.command}
          </span>
          <span className="truncate text-xs text-coval-text-muted">
            {command.description}
          </span>
        </button>
      ))}
    </div>
  )
}
